import { createElement, type ComponentType } from 'react'
import { Navigate, useParams, type RouteObject } from 'react-router-dom'
import { AppPaths } from '@/config/app-paths'
import { createPermissionGuardedPage } from '@/shared/components/create-permission-guarded-page'
import { importWithChunkReload } from '@/shared/lib/chunk-load-recovery'
import { PayrollPermissions } from './permissions'

function guarded(
  perm: string | readonly string[],
  Page: ComponentType,
  mode: 'all' | 'any' = 'all',
): ComponentType {
  return createPermissionGuardedPage(perm, Page, { mode, administratorBypass: true })
}

async function lazyGuardedRoute(
  importPage: () => Promise<{ default: ComponentType } | Record<string, ComponentType>>,
  exportName: string,
  perm: string | readonly string[],
  mode: 'all' | 'any' = 'all',
): Promise<{ Component: ComponentType }> {
  const mod = await importWithChunkReload(importPage)
  const Page =
    'default' in mod && mod.default
      ? mod.default
      : (mod as Record<string, ComponentType>)[exportName]
  if (!Page) {
    throw new Error(`Payroll route page export "${exportName}" not found`)
  }
  return { Component: guarded(perm, Page, mode) }
}

/** Payroll feature routes with capability gates. */
export const payrollRoutes: RouteObject[] = [
  {
    path: 'payroll',
    lazy: async () => {
      const { PayrollHubPage } = await import('./pages/payroll-hub-page')
      return { Component: PayrollHubPage }
    },
  },
  {
    path: 'payroll/how-it-works',
    lazy: async () => {
      const { PayrollHowItWorksPage } = await import('./pages/payroll-how-it-works-page')
      return { Component: PayrollHowItWorksPage }
    },
  },
  {
    path: 'payroll/accounts',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-accounts-dashboard-page'),
        'PayrollAccountsDashboardPage',
        PayrollPermissions.accountsWorkspaceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/accounts/people',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-accounts-people-page'),
        'PayrollAccountsPeoplePage',
        PayrollPermissions.accountsWorkspaceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/accounts/clients',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-accounts-clients-page'),
        'PayrollAccountsClientsPage',
        PayrollPermissions.accountsWorkspaceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-admin-console-page'),
        'PayrollAdminConsolePage',
        PayrollPermissions.adminConsoleAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/dashboard',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-admin-dashboard-page'),
        'PayrollAdminDashboardPage',
        PayrollPermissions.adminConsoleAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/formulas',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-formulas-page'),
        'PayrollFormulasPage',
        PayrollPermissions.manage,
      ),
  },
  {
    path: 'payroll/admin/identities',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-identity-list-page'),
        'PayrollIdentityListPage',
        PayrollPermissions.identityAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/identities/:employeeId',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-identity-detail-page'),
        'PayrollIdentityDetailPage',
        PayrollPermissions.identityAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/assignments',
    lazy: async () => ({
      Component: function RedirectAssignmentsToDeployment() {
        return createElement(Navigate, { to: AppPaths.workforceDeployment, replace: true })
      },
    }),
  },
  {
    path: 'payroll/admin/deployment',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/workforce-deployment-page'),
        'WorkforceDeploymentPage',
        PayrollPermissions.workforceDeploy,
        'all',
      ),
  },
  {
    path: 'payroll/admin/deployment/detail',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/workforce-deployment-detail-page'),
        'WorkforceDeploymentDetailPage',
        PayrollPermissions.workforceDeploy,
        'all',
      ),
  },
  {
    path: 'payroll/admin/staff',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-staff-roster-page'),
        'PayrollAdminStaffRosterPage',
        PayrollPermissions.adminConsoleAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/public-holidays',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-public-holidays-page'),
        'PayrollAdminPublicHolidaysPage',
        PayrollPermissions.adminConsoleAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/staff-cost-rates',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-staff-cost-rates-page'),
        'PayrollAdminStaffCostRatesPage',
        PayrollPermissions.adminConsoleAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/pay-models',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-pay-models-page'),
        'PayrollAdminPayModelsPage',
        PayrollPermissions.adminConsoleAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/settlements',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-settlements-inbox-page'),
        'PayrollSettlementsInboxPage',
        PayrollPermissions.settlementAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/staff/:employeeId',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-staff-card-page'),
        'PayrollAdminStaffCardPage',
        PayrollPermissions.identityAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/runs/:runId',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-period-board-page'),
        'PayrollAdminPeriodBoardPage',
        PayrollPermissions.payRunAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/periods/:periodId',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-period-overview-page'),
        'PayrollAdminPeriodOverviewPage',
        PayrollPermissions.payRunAccess,
        'any',
      ),
  },
  {
    path: 'payroll/admin/pay-months/:anchorMonth',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./admin/pages/payroll-admin-pay-month-overview-page'),
        'PayrollAdminPayMonthOverviewPage',
        PayrollPermissions.payRunAccess,
        'any',
      ),
  },
  {
    path: 'payroll/runs',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-runs-page'),
        'PayrollRunsPage',
        PayrollPermissions.payRunAccess,
        'any',
      ),
  },
  {
    path: 'payroll/runs/:runId',
    lazy: async () => {
      function RedirectToAdminPeriodBoard() {
        const { runId = '' } = useParams<{ runId: string }>()
        return createElement(Navigate, {
          to: AppPaths.payrollAdminRun(runId),
          replace: true,
        })
      }
      return { Component: RedirectToAdminPeriodBoard }
    },
  },
  {
    path: 'payroll/supervisor-rosters',
    lazy: async () => {
      const { SupervisorManagementPage } = await import('./pages/supervisor-management-page')
      return { Component: guarded(PayrollPermissions.manage, SupervisorManagementPage) }
    },
  },
  {
    path: 'payroll/daily-attendance',
    lazy: async () => {
      const { PayrollDailyAttendancePage } = await import('./pages/payroll-daily-attendance-page')
      return { Component: guarded(PayrollPermissions.manage, PayrollDailyAttendancePage) }
    },
  },
  {
    path: 'payroll/daily-attendance/employee/:employeeId',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-employee-attendance-detail-page'),
        'PayrollEmployeeAttendanceDetailPage',
        PayrollPermissions.manage,
      ),
  },
  {
    path: 'payroll/attendance-issues',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-attendance-issues-page'),
        'PayrollAttendanceIssuesPage',
        PayrollPermissions.teamAttendanceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/attendance-notifications',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-attendance-notifications-page'),
        'PayrollAttendanceNotificationsPage',
        PayrollPermissions.teamAttendanceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/mobile-app',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/supervisor-mobile-app-registrations-page'),
        'SupervisorMobileAppRegistrationsPage',
        PayrollPermissions.teamAttendanceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/team-attendance',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-team-attendance-page'),
        'PayrollTeamAttendancePage',
        PayrollPermissions.teamAttendanceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/team-attendance/employee/:employeeId/punch/:date',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-team-punch-day-page'),
        'PayrollTeamPunchDayPage',
        PayrollPermissions.teamAttendanceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/team-availability',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-team-availability-page'),
        'PayrollTeamAvailabilityPage',
        PayrollPermissions.teamAttendanceAccess,
        'any',
      ),
  },
  {
    path: 'payroll/employee-workspace',
    lazy: () =>
      lazyGuardedRoute(
        () => import('./pages/payroll-employee-workspace-page'),
        'PayrollEmployeeWorkspacePage',
        PayrollPermissions.employeeSetup,
      ),
  },
]
